import { useMemo, useState } from 'react'
import { Card, DatePicker, Select, Space, Tabs, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { type Dayjs } from 'dayjs'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { defaultDayRange, rangeToIso } from '@/shared/lib/dayRange'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import {
  DELIVERY_CHANNELS,
  fetchDeadLetters,
  fetchMessagePage,
  type DeliveryChannel,
  type MessageView,
} from '@/features/notify/notifyApi'
import { NamedCode } from '@/features/notify/NamedCode'
import { deliveryErrorText, eventName, templateName } from '@/features/notify/notifyLabels'

const { RangePicker } = DatePicker

const MESSAGE_STATES = ['QUEUED', 'SENT', 'DEAD', 'SKIPPED'] as const

const STATE_COLOR: Record<string, string> = {
  QUEUED: 'blue',
  SENT: 'green',
  DEAD: 'red',
  SKIPPED: 'default',
}

const defaultRange = (): [Dayjs, Dayjs] => defaultDayRange(7)

function commonColumns(t: TFunction): ColumnsType<MessageView> {
  return [
    {
      key: 'createdAt',
      title: t('notify.message.createdAt'),
      width: 170,
      render: (_, row) => (
        <Typography.Text title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</Typography.Text>
      ),
    },
    {
      key: 'eventType',
      title: t('notify.subscription.eventType'),
      width: 200,
      render: (_, row) => <NamedCode name={eventName(row.eventType, t)} code={row.eventType} />,
    },
    {
      key: 'channel',
      title: t('notify.channel'),
      width: 110,
      render: (_, row) => t(`notify.channelName.${row.channel}`),
    },
    {
      key: 'recipient',
      title: t('notify.message.recipient'),
      width: 170,
      // ⚠️ TO'LIQ qiymat, MASKALANMAGAN — backend javadoc'i: operator
      // yetkazish muammosini tekshirishi kerak (bu sahifa `notify.message.read`
      // bilan himoyalangan, keng doiraga ochiq emas)
      render: (_, row) => <Typography.Text copyable>{row.recipient}</Typography.Text>,
    },
    {
      key: 'templateCode',
      title: t('notify.subscription.templateCode'),
      width: 180,
      render: (_, row) => <NamedCode name={templateName(row.templateCode, t)} code={row.templateCode} />,
    },
    {
      key: 'state',
      title: t('notify.message.state'),
      width: 110,
      render: (_, row) => <Tag color={STATE_COLOR[row.state] ?? 'default'}>{t(`notify.messageState.${row.state}`, { defaultValue: row.state })}</Tag>,
    },
    {
      key: 'attempts',
      title: t('notify.message.attempts'),
      width: 100,
      align: 'right',
      render: (_, row) => row.attempts,
    },
    {
      key: 'lastError',
      title: t('notify.message.lastError'),
      render: (_, row) => deliveryErrorText(row.lastError, t) ?? t('common.dash'),
    },
  ]
}

/**
 * Yuborish jurnali — ikki bo'lim: sana oralig'i bo'yicha filtrlangan to'liq
 * jurnal va O'LIK XABARLAR (sana filtrisiz — "hozir nima buzilgan" ro'yxati,
 * `MessageController.deadLetters` javadoc'i). Ikkalasi alohida, chunki
 * o'lik xabarlar operator aralashuvini talab qiladi va sana filtriga
 * yashirinib qolmasligi kerak.
 */
export function NotifyMessagesPage() {
  const { t } = useTranslation()
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange)
  const [channel, setChannel] = useState<DeliveryChannel | null>(null)
  const [state, setState] = useState<string | null>(null)

  // ⚠️ TENANT zonasida: `dayjs()` brauzernikini olardi (`dayRange.ts`)
  const { from, to } = rangeToIso(range)

  const filter = useMemo(() => ({ channel, state, from, to }), [channel, state, from, to])
  const fetchPage = useMemo(() => fetchMessagePage(filter), [filter])
  const fetchDead = useMemo(() => fetchDeadLetters(), [])

  const columns = useMemo(() => commonColumns(t), [t])

  return (
    <>
      <PageHeader title={t('nav.notifyMessages')} description={t('pageIntro.notifyMessages')} />

      <Tabs
        items={[
          {
            key: 'all',
            label: t('notify.message.tabAll'),
            children: (
              <Card size="small">
                <Space wrap style={{ marginBottom: 16 }}>
                  <RangePicker
                    value={range}
                    onChange={(value) => {
                      if (value?.[0] && value[1]) setRange([value[0], value[1]])
                    }}
                    allowClear={false}
                    format="DD.MM.YYYY"
                  />
                  <Select<DeliveryChannel | 'ALL'>
                    style={{ width: 160 }}
                    value={channel ?? 'ALL'}
                    onChange={(value) => setChannel(value === 'ALL' ? null : value)}
                    options={[
                      { value: 'ALL', label: t('subscribers.filterAll') },
                      ...DELIVERY_CHANNELS.map((c) => ({ value: c, label: t(`notify.channelName.${c}`) })),
                    ]}
                  />
                  <Select<string>
                    style={{ width: 160 }}
                    value={state ?? 'ALL'}
                    onChange={(value) => setState(value === 'ALL' ? null : value)}
                    options={[
                      { value: 'ALL', label: t('notify.message.state') },
                      ...MESSAGE_STATES.map((s) => ({ value: s, label: t(`notify.messageState.${s}`) })),
                    ]}
                  />
                </Space>

                <DataTable<MessageView>
                  queryKey={queryKeys.notify.messages(filter)}
                  fetchPage={fetchPage}
                  columns={columns}
                  rowKey={(row) => row.id}
                  tableId="notifyMessages"
                  showTotal={false}
                  scrollX={1200}
                  emptyTitle={t('notify.message.empty')}
                />
              </Card>
            ),
          },
          {
            key: 'dead',
            label: t('notify.message.tabDead'),
            children: (
              <Card size="small">
                <Typography.Paragraph type="secondary">{t('notify.message.deadHint')}</Typography.Paragraph>
                <DataTable<MessageView>
                  queryKey={queryKeys.notify.deadLetters}
                  fetchPage={fetchDead}
                  columns={columns}
                  rowKey={(row) => row.id}
                  tableId="notifyDeadLetters"
                  showTotal={false}
                  scrollX={1200}
                  emptyTitle={t('notify.message.deadEmpty')}
                />
              </Card>
            ),
          },
        ]}
      />
    </>
  )
}
